import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { LoadingState } from '@lcs/shared/api';
import { Store, select } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { filter, first, map, switchMap, withLatestFrom } from 'rxjs/operators';
import {
   getIsLoggedIn,
   getLoginCallState,
   hasSelectedPrivilege,
   selectPrivileges,
} from '../authentication/state/authentication.selector';
import { getSelectedAccountCallState, getSelectedAccountId } from '../store/select-account/select-account.selector';

@Injectable({
   providedIn: 'root',
})
export class PrivilegeGuard  {
   constructor(private router: Router, private store: Store, private location: Location) {}

   canActivate(
      route: ActivatedRouteSnapshot,
      state: RouterStateSnapshot
   ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
      const isSystemUserPage = route.data.isSystemUserPage ?? false;
      const isDetailPage = route.data.isDetailPage ?? false;
      const privilege = route.data?.privilege;
      if (privilege) {
         return this.store.pipe(
            withLatestFrom(
               this.store.pipe(select(getLoginCallState)),
               this.store.pipe(select(getSelectedAccountCallState))
            ),
            filter(([action, loginCallState, accountCallState]) => {
               return loginCallState === LoadingState.Loaded && accountCallState === LoadingState.Loaded;
            }),
            switchMap(() => {
               return combineLatest([
                  this.store.pipe(select(getIsLoggedIn)),
                  this.store.pipe(select(getSelectedAccountId)),
                  this.store.pipe(select(selectPrivileges)),
               ]);
            }),
            first(),
            map(([isLoggedIn, accountId, privileges]) => {
               if (
                  isLoggedIn &&
                  (isDetailPage || hasSelectedPrivilege(privilege, isSystemUserPage ? null : accountId, privileges))
               ) {
                  return true;
               } else {
                  this.router.navigate(['access-denied'], { skipLocationChange: true }).then(() => {
                     // this is required because skipLocationChange doesn't do what it's supposed to
                     // see https://github.com/angular/angular/issues/16981
                     // and https://github.com/angular/angular/issues/17004
                     // this causes the URL to flicker upon first navigation but it's
                     // better than setting the url to '/access-denied'
                     this.location.replaceState(state.url);
                  });
                  return false;
               }
            })
         );
      } else {
         throw new Error('PrivilegeGuard does not have a privilege set on the route data');
      }
   }
}
